import axios from 'axios';
import store from '../store';
import router from '../router';

// API Base URL: same-origin reverse proxy (nginx/Caddy/Ingress) /api/* → backend
const baseURL = '/api';

// Refresh token sırasında eşzamanlı 401'ler için tek bir refresh isteği
let refreshPromise = null;

const apiClient = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Request Interceptor
apiClient.interceptors.request.use(
  (config) => {
    // FormData: axios flatten sonrası önceki adımlarda bile olsa json Content-Type geri gelebilir;
    // XHR boundary'yi kendisi koymalı — common/post üzerindeki Content-Type'ı kaldır.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const stripContentType = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        delete obj['Content-Type'];
        delete obj['content-type'];
      };
      stripContentType(config.headers);
      if (config.headers.common) stripContentType(config.headers.common);
      const m = (config.method || 'get').toLowerCase();
      if (config.headers[m]) stripContentType(config.headers[m]);
    }

    // Loading state'i başlat (blob/stream istekleri veya açıkça istenmeyenler hariç)
    if (!config.skipLoading) {
      store.commit('ui/SET_LOADING', true);
    }

    // /auth/refresh için Authorization gönderme (sadece body'de refresh_token)
    const isRefreshRequest = config.url && config.url.includes('/auth/refresh');
    if (!isRefreshRequest) {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Request logla (development'ta)
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 API Request:', {
        method: config.method && config.method.toUpperCase(),
        url: config.url,
        data: config.data,
        params: config.params,
      });
    }

    return config;
  },
  (error) => {
    if (!error.config?.skipLoading) {
      store.commit('ui/SET_LOADING', false);
    }
    console.error('Request Error:', error);
    return Promise.reject(error);
  },
);

// Response Interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Loading state'i bitir
    if (!response.config?.skipLoading) {
      store.commit('ui/SET_LOADING', false);
    }

    // Response logla (development'ta)
    if (process.env.NODE_ENV === 'development') {
      console.log('📥 API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data,
      });
    }

    return response;
  },
  async (error) => {
    // Loading state'i bitir
    if (!error.config?.skipLoading) {
      store.commit('ui/SET_LOADING', false);
    }

    // Error logla
    console.error('Response Error:', error);

    if (error.response) {
      const { status, data } = error.response;
      const originalRequest = error.config;

      // Sessiz istekler: toast yok (401 hariç — oturum yenileme gerekir)
      if (originalRequest?.skipErrorNotification && status !== 401) {
        return Promise.reject(error);
      }

      if (status === 401) {
        // Refresh endpoint 401 döndü → refresh_token geçersiz, login'e yönlendir
        const isRefreshRequest = originalRequest.url && originalRequest.url.includes('/auth/refresh');
        if (isRefreshRequest) {
          store.dispatch('auth/logout');
          router.push('/login');
          store.dispatch('ui/showNotification', {
            type: 'error',
            message: 'Your session has expired. Please log in again.',
            timeout: 5000,
          });
          return Promise.reject(error);
        }

        // Zaten refresh sonrası tekrar denendi, yine 401 → login'e yönlendir
        if (originalRequest._retriedAfterRefresh) {
          store.dispatch('auth/logout');
          router.push('/login');
          store.dispatch('ui/showNotification', {
            type: 'error',
            message: 'Your session has expired. Please log in again.',
            timeout: 5000,
          });
          return Promise.reject(error);
        }

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          store.dispatch('auth/logout');
          router.push('/login');
          store.dispatch('ui/showNotification', {
            type: 'error',
            message: 'Your session has expired. Please log in again.',
            timeout: 5000,
          });
          return Promise.reject(error);
        }

        // Tek bir refresh isteği (eşzamanlı 401'ler için)
        if (!refreshPromise) {
          refreshPromise = store.dispatch('auth/refreshToken')
            .then((newToken) => {
              refreshPromise = null;
              return newToken;
            })
            .catch((err) => {
              refreshPromise = null;
              // Store refreshToken action zaten logout dispatch ediyor; login'e yönlendir
              router.push('/login');
              throw err;
            });
        }

        return refreshPromise.then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          originalRequest._retriedAfterRefresh = true;
          return apiClient(originalRequest);
        }).catch((err) => Promise.reject(err));
      }

      switch (status) {
      case 403:
        // Forbidden - Yetkisiz erişim
        store.dispatch('ui/showNotification', {
          type: 'error',
          message: 'Bu işlem için yetkiniz bulunmuyor.',
          timeout: 5000,
        });
        break;

      case 404:
        // Not Found
        store.dispatch('ui/showNotification', {
          type: 'error',
          message: 'İstenen kaynak bulunamadı.',
          timeout: 5000,
        });
        break;

      case 422: {
        // Validation Error
        const validationErrors = data.errors || {};
        const errorMessages = Object.values(validationErrors).flat();
        store.dispatch('ui/showNotification', {
          type: 'error',
          message: errorMessages.join(', ') || 'Validation error.',
          timeout: 5000,
        });
        break;
      }

      case 500:
        // Server Error
        store.dispatch('ui/showNotification', {
          type: 'error',
          message: 'Server error. Please try again later.',
          timeout: 5000,
        });
        break;

      default: {
        // Diğer hatalar
        const message = data.message || error.message || 'An error occurred.';
        store.dispatch('ui/showNotification', {
          type: 'error',
          message,
          timeout: 5000,
        });
        break;
      }
      }
    } else if (error.request) {
      // Network hatası
      store.dispatch('ui/showNotification', {
        type: 'error',
        message: 'Bağlantı hatası. İnternet bağlantınızı kontrol edin.',
        timeout: 5000,
      });
    } else {
      // Diğer hatalar
      store.dispatch('ui/showNotification', {
        type: 'error',
        message: error.message || 'Beklenmeyen bir hata oluştu.',
        timeout: 5000,
      });
    }

    return Promise.reject(error);
  },
);

export default apiClient;
